import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface TestGroup {
  Id: string;
  ExternalId: string;
  Name: string;
  Description: string;
  Configuration: TestConfiguration;
  CategoryId: string;
  Category?: any;
}

export interface TestConfiguration {
  TenantId: string;
  IntegrationId: string;
  ProcessId: string;
  AuthenticationTokenSecretId: string;
}

export interface TestRunData {
  testRunId: string;
  testGroupName: string;
  testCasesCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class TestGroupService {
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}/testing`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getGroups(): Promise<Array<TestGroup> | null> {
    try {
      const response = await this.api.get("/group");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getGroup(id: string): Promise<TestGroup | null> {
    try {
      const response = await this.api.get("/group/" + id);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateGroup(testGroup: TestGroup): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.put("/group/" + testGroup.Id, testGroup);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createGroup(testGroup: TestGroup): Promise<string | null> {
    try {
      const response = await this.api.post("/group", testGroup);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteGroup(id: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete("/group/" + id);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async startTestRun(id: string): Promise<TestRunData | null> {
    try {
      const response = await this.api.post("/group/" + id + "/run");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
